import '../../../styles/om-module.css';
import '../../../styles/om-modals.css';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    ChevronLeft, ChevronRight, ChevronDown, Calendar as CalendarIcon,
    Clock, MapPin, Users, Repeat, Plus, X, Check, Search,
    Edit2, Trash2, Sun, Zap, Wind, Wrench, Droplets, Scissors,
    AlertTriangle, BarChart3, RefreshCw, Download, Filter, Bell,
    Activity, ArrowLeft, ArrowRight
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import backendApi from '../../../services/backendApi';
import { useSiteDisplayName } from '../../../utils/siteUtils';

// ─── Types ────────────────────────────────────────────────────────────────────
type TaskType = 'module-cleaning' | 'grass-cutting' | 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly' | 'inverter' | 'tracker' | 'electrical' | 'custom';
type TaskStatus = 'scheduled' | 'in-progress' | 'completed' | 'overdue' | 'cancelled';
type CalView = 'month' | 'week' | 'day';

interface PMTask {
    id: string;
    wo_number?: string;
    is_escalated?: boolean;
    title: string;
    type: TaskType;
    date: string; // ISO yyyy-mm-dd
    startTime: string;
    endTime: string;
    assignedTo: string;
    location: string;
    status: TaskStatus;
    priority: 'low' | 'medium' | 'high' | 'critical';
    description?: string;
    isRecurring: boolean;
    recurringPattern?: string;
    color: string;
    site?: string;
    equipmentNames?: string[];
}

interface NewTaskForm {
    title: string;
    type: TaskType;
    date: string;
    startTime: string;
    endTime: string;
    assignedTo: string;
    location: string;
    description: string;
    priority: PMTask['priority'];
    isRecurring: boolean;
    recurringPattern: string;
    equipmentNames: string[];
}

interface AssignableUser {
    id: string;
    username: string;
    displayName: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
const TYPE_CONFIG: Record<TaskType, { label: string; color: string; bg: string; text: string; icon: React.ReactNode }> = {
    // Task type is a categorical encoding, not a status. Each chip also carries
    // an icon and a label, so colour is a secondary cue — which lets the set sit
    // on the brand ramp (led by the blues, extended with muted accents that
    // harmonise rather than compete) instead of the previous rainbow.
    // STATUS_CONFIG below stays semantic and is deliberately untouched.
    'module-cleaning': { label: 'Module Cleaning', color: '#0084CF', bg: 'bg-[#F2F8FC]', text: 'text-[#1C4D88]', icon: <Droplets size={14} /> },
    'grass-cutting': { label: 'Grass Cutting', color: '#17807D', bg: 'bg-[#F2F8FC]', text: 'text-[#0066A3]', icon: <Scissors size={14} /> },
    'daily': { label: 'Daily Inspection', color: '#3EA3DA', bg: 'bg-[#F2F8FC]', text: 'text-[#0066A3]', icon: <Sun size={14} /> },
    'weekly': { label: 'Weekly PM', color: '#0066A3', bg: 'bg-[#F2F8FC]', text: 'text-[#1C4D88]', icon: <CalendarIcon size={14} /> },
    'monthly': { label: 'Monthly PM', color: '#1C4D88', bg: 'bg-[#E3F0F9]', text: 'text-[#1C4D88]', icon: <BarChart3 size={14} /> },
    'quarterly': { label: 'Quarterly PM', color: '#0D2747', bg: 'bg-[#E3F0F9]', text: 'text-[#0D2747]', icon: <Activity size={14} /> },
    'yearly': { label: 'Yearly PM', color: '#6E7F99', bg: 'bg-[#F6F8FB]', text: 'text-[#52606F]', icon: <RefreshCw size={14} /> },
    'inverter': { label: 'Inverter Service', color: '#8AC4E6', bg: 'bg-[#F2F8FC]', text: 'text-[#0066A3]', icon: <Zap size={14} /> },
    'tracker': { label: 'Tracker Maint.', color: '#4A6FA5', bg: 'bg-[#F2F8FC]', text: 'text-[#1C4D88]', icon: <Wind size={14} /> },
    'electrical': { label: 'Electrical Test', color: '#0084CF', bg: 'bg-[#F2F8FC]', text: 'text-[#1C4D88]', icon: <Zap size={14} /> },
    'custom': { label: 'Custom Task', color: '#64748b', bg: 'bg-slate-50', text: 'text-slate-700', icon: <Wrench size={14} /> },
};

const STATUS_CONFIG: Record<TaskStatus, { label: string; dot: string; badge: string }> = {
    'scheduled': { label: 'Scheduled', dot: 'bg-[#3EA3DA]', badge: 'bg-[#E3F0F9] text-[#1C4D88] border-[#8AC4E6]' },
    'in-progress': { label: 'In Progress', dot: 'bg-amber-400', badge: 'bg-amber-100 text-amber-800 border-amber-300' },
    'completed': { label: 'Completed', dot: 'bg-green-400', badge: 'bg-green-100 text-green-800 border-green-300' },
    'overdue': { label: 'Overdue', dot: 'bg-red-400', badge: 'bg-red-100 text-red-800 border-red-300' },
    'cancelled': { label: 'Cancelled', dot: 'bg-gray-400', badge: 'bg-gray-100 text-gray-700 border-gray-300' },
};

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAY_SHORT = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const DEFAULT_FORM: NewTaskForm = { title: '', type: 'daily', date: '', startTime: '08:00', endTime: '10:00', assignedTo: '', location: '', description: '', priority: 'medium', isRecurring: false, recurringPattern: '', equipmentNames: [] };

// ─── Component ────────────────────────────────────────────────────────────────
const PMCalendar: React.FC = () => {
    const { site } = useParams<{ site: string }>();
    const siteDisplayName = useSiteDisplayName(site);
    const routeNavigate = useNavigate();

    // ── Helper: Generate unique instance ID for recurring tasks ──────────────
    const getInstanceId = (taskId: string, instanceDate: string): string => {
        return `${taskId}:${instanceDate}`;
    };

    // ── Helper: Get actual status for a task instance ────────────────────────
    const getInstanceStatus = (task: PMTask, instanceDate: string): TaskStatus => {
        if (task.isRecurring) {
            const instanceId = getInstanceId(task.id, instanceDate);
            return instanceStatuses[instanceId] ?? task.status;
        }
        return task.status;
    };

    const [tasks, setTasks] = useState<PMTask[]>([]);
    const [loading, setLoading] = useState(false);
    const [currentDate, setCurrentDate] = useState(new Date());
    const [view, setView] = useState<CalView>('month');
    const [selectedTask, setSelectedTask] = useState<PMTask | null>(null);
    const [selectedInstanceDate, setSelectedInstanceDate] = useState<string | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [editTask, setEditTask] = useState<PMTask | null>(null);
    const [form, setForm] = useState<NewTaskForm>(DEFAULT_FORM);
    const [filterType, setFilterType] = useState('all');
    const [filterStatus, setFilterStatus] = useState('all');
    const [searchQ, setSearchQ] = useState('');
    const [pickerYear, setPickerYear] = useState(new Date().getFullYear());
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [activeView, setActiveView] = useState<string>('Month');
    const [assignableUsers, setAssignableUsers] = useState<AssignableUser[]>([]);
    const [usersLoading, setUsersLoading] = useState(false);
    const [inventoryEquipments, setInventoryEquipments] = useState<string[]>([]);
    const [equipsLoading, setEquipsLoading] = useState(false);
    const [equipSearch, setEquipSearch] = useState('');
    const [showEquipDropdown, setShowEquipDropdown] = useState(false);
    const equipmentDropdownRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!showEquipDropdown) {
            return;
        }

        const handleOutsideClick = (event: MouseEvent | TouchEvent) => {
            const target = event.target as Node | null;
            if (!target) return;
            if (equipmentDropdownRef.current && !equipmentDropdownRef.current.contains(target)) {
                setShowEquipDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleOutsideClick);
        document.addEventListener('touchstart', handleOutsideClick);

        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
            document.removeEventListener('touchstart', handleOutsideClick);
        };
    }, [showEquipDropdown]);

    // Date-range filter for API queries
    const [filterStartDate, setFilterStartDate] = useState('');
    const [filterEndDate, setFilterEndDate] = useState('');
    const [showDateRangeFilter, setShowDateRangeFilter] = useState(false);

    // Instance-level status overrides for recurring tasks (key: "taskId:instanceDate", value: status)
    const [instanceStatuses, setInstanceStatuses] = useState<Record<string, TaskStatus>>({});
    const [flowLoading, setFlowLoading] = useState(false);
    const [flowError, setFlowError] = useState('');
    const [flowSuccess, setFlowSuccess] = useState<any | null>(null);

    const [conflictWarning, setConflictWarning] = useState<string | null>(null);

    useEffect(() => {
        if (!showModal || !site || !form.date || !form.equipmentNames || form.equipmentNames.length === 0) {
            setConflictWarning(null);
            return;
        }
        const checkConflict = async () => {
            try {
                const res = await (backendApi as any).checkPMEquipmentConflict?.({
                    site_code: site,
                    date: form.date,
                    equipment_names: form.equipmentNames,
                    exclude_event_id: editTask?.id || null,
                });
                if (res?.has_conflict && Array.isArray(res.conflicts) && res.conflicts.length > 0) {
                    const conf = res.conflicts[0];
                    setConflictWarning(`⚡ Equipment Conflict Warning: ${conf.equipment_names.join(', ')} is already scheduled on ${form.date} (${conf.wo_number || conf.title}).`);
                } else {
                    setConflictWarning(null);
                }
            } catch {
                setConflictWarning(null);
            }
        };
        checkConflict();
    }, [showModal, form.date, form.equipmentNames, editTask?.id, site]);

    // ── Site Users For Dynamic Assignment ──────────────────────────────────
    useEffect(() => {
        if (!site) {
            setAssignableUsers([]);
            return;
        }

        const loadSiteUsers = async () => {
            setUsersLoading(true);
            try {
                const siteId = Number(localStorage.getItem('site_id') || sessionStorage.getItem('site_id') || 0) || null;
                let users: any[] = [];

                // Primary source: PM module endpoint (works for site-admin and general users too).
                const pmUsersRes = await (backendApi as any).getPMAssignableUsers?.(site);
                users = Array.isArray(pmUsersRes?.users) ? pmUsersRes.users : [];

                // Fallback: admin site users API if explicit site id exists.
                if (!users.length && siteId) {
                    const res = await (backendApi as any).getSiteUsers?.(siteId);
                    users = Array.isArray(res?.users) ? res.users : [];
                }

                // Last fallback: admin users manage API with explicit site scope.
                if (!users.length) {
                    const res2 = await (backendApi as any).getUsers?.(null, siteId, site, { skipStorageFallback: true });
                    users = Array.isArray(res2?.users) ? res2.users : [];
                }

                const normalizedSite = String(site || '').toUpperCase();
                const mapped: AssignableUser[] = users
                    .filter((u: any) => {
                        const userSiteCode = String(u?.site?.site_code || u?.site_code || '').toUpperCase();
                        return !userSiteCode || userSiteCode === normalizedSite;
                    })
                    .map((u: any) => {
                        const id = String(u?.id || u?.user_id || u?.pk || '');
                        const first = String(u?.first_name || '').trim();
                        const last = String(u?.last_name || '').trim();
                        const fullName = String(u?.display_name || u?.full_name || `${first} ${last}`.trim() || '').trim();
                        const username = String(u?.username || '').trim();
                        const displayName = fullName || username || `User ${id}`;
                        return { id, username, displayName };
                    })
                    .filter((u: AssignableUser) => !!u.id)
                    .reduce((acc: AssignableUser[], user: AssignableUser) => {
                        if (!acc.some(existing => existing.id === user.id)) {
                            acc.push(user);
                        }
                        return acc;
                    }, []);

                setAssignableUsers(mapped);
            } catch {
                setAssignableUsers([]);
            } finally {
                setUsersLoading(false);
            }
        };

        loadSiteUsers();
    }, [site]);

    // ── Inventory Equipment Names ────────────────────────────────────────────
    useEffect(() => {
        if (!site) { setInventoryEquipments([]); return; }
        const loadEquipments = async () => {
            setEquipsLoading(true);
            try {
                const res = await (backendApi as any).getInventoryEquipmentNames?.(site);
                setInventoryEquipments(Array.isArray(res?.equipment_names) ? res.equipment_names : []);
            } catch {
                setInventoryEquipments([]);
            } finally {
                setEquipsLoading(false);
            }
        };
        loadEquipments();
    }, [site]);

    const mapEventToTask = useCallback((e: any): PMTask => ({
        ...e,
        id: String(e.id),
        wo_number: e.wo_number || '',
        is_escalated: !!e.is_escalated,
        date: e.date?.split('T')[0] || e.date,
        startTime: e.start_time || e.startTime || '09:00',
        endTime: e.end_time || e.endTime || '10:00',
        assignedTo: e.assigned_to || e.assignedTo || '',
        isRecurring: e.is_recurring ?? e.isRecurring ?? false,
        recurringPattern: e.recurring_pattern || e.recurringPattern || '',
        color: TYPE_CONFIG[e.type as TaskType]?.color || '#0084CF',
        equipmentNames: Array.isArray(e.equipment_names) ? e.equipment_names : [],
    }), []);

    const loadTasks = useCallback(async () => {
        if (!site) return;
        setLoading(true);
        try {
            const res = await (backendApi as any).getPMCalendarEvents?.(site, filterStartDate || undefined, filterEndDate || undefined);
            if (res?.success && Array.isArray(res.events)) {
                const today = new Date().toISOString().slice(0, 10);
                const overdueIds: string[] = [];
                const mapped: PMTask[] = res.events.map((e: any) => {
                    const task = mapEventToTask(e);
                    // Auto-mark overdue: past date + still "scheduled"
                    if (task.date < today && task.status === 'scheduled' && !task.isRecurring) {
                        overdueIds.push(task.id);
                        return { ...task, status: 'overdue' as TaskStatus };
                    }
                    return task;
                });
                setTasks(mapped);
                // Sync overdue status to backend silently
                if (overdueIds.length > 0) {
                    Promise.all(
                        overdueIds.map(id => (backendApi as any).updatePMCalendarStatus?.(id, 'overdue'))
                    ).catch(() => {});
                }
            } else {
                setTasks([]);
            }
        } catch {
            setTasks([]);
        }
        setLoading(false);
    }, [site, filterStartDate, filterEndDate, mapEventToTask]);

    // ── API Integration ─────────────────────────────────────────────────────
    useEffect(() => {
        loadTasks();
    }, [loadTasks]);

    // ── Calendar Grid Helpers ───────────────────────────────────────────────
    const calDays = useCallback(() => {
        const y = currentDate.getFullYear(), m = currentDate.getMonth();
        const first = new Date(y, m, 1).getDay();
        const total = new Date(y, m + 1, 0).getDate();
        const cells: (Date | null)[] = Array(first).fill(null);
        for (let d = 1; d <= total; d++) cells.push(new Date(y, m, d));
        while (cells.length % 7 !== 0) cells.push(null);
        return cells;
    }, [currentDate]);

    const weekDays = useCallback(() => {
        const d = new Date(currentDate);
        const day = d.getDay();
        d.setDate(d.getDate() - day);
        return Array.from({ length: 7 }, (_, i) => { const dd = new Date(d); dd.setDate(d.getDate() + i); return dd; });
    }, [currentDate]);

    const isToday = (d: Date) => {
        const t = new Date();
        return d.getDate() === t.getDate() && d.getMonth() === t.getMonth() && d.getFullYear() === t.getFullYear();
    };

    const fmtDate = (d: Date) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    };

    const isMatchRecurringPattern = (task: PMTask, targetDate: Date) => {
        if (!task.isRecurring || !task.recurringPattern) return false;

        // Parse the initial date (YYYY-MM-DD)
        const [y, m, d] = task.date.split('-').map(Number);
        const taskDate = new Date(y, m - 1, d);

        // Only recur on or after the original date
        if (targetDate < taskDate) return false;

        // Normalizing both to midnight for consistency
        const tDate = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate());
        const oDate = new Date(taskDate.getFullYear(), taskDate.getMonth(), taskDate.getDate());

        const diffTime = tDate.getTime() - oDate.getTime();
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

        const pattern = task.recurringPattern.toLowerCase();

        if (pattern === 'daily') return true;
        if (pattern === 'weekly') return diffDays % 7 === 0;
        if (pattern === 'bi-weekly') return diffDays % 14 === 0;
        if (pattern === 'monthly') return tDate.getDate() === oDate.getDate();
        if (pattern === 'quarterly') {
            const monthDiff = (tDate.getFullYear() - oDate.getFullYear()) * 12 + (tDate.getMonth() - oDate.getMonth());
            return tDate.getDate() === oDate.getDate() && monthDiff % 3 === 0;
        }
        if (pattern === 'yearly') {
            return tDate.getDate() === oDate.getDate() && tDate.getMonth() === oDate.getMonth();
        }

        return false;
    };

    const tasksForDate = (d: Date) => {
        const key = fmtDate(d);
        return filtered.filter(t => {
            if (t.date === key) return true;
            return isMatchRecurringPattern(t, d);
        });
    };

    // ── Filtered Tasks ──────────────────────────────────────────────────────
    const filtered = tasks.filter(t => {
        const ms = filterStatus === 'all' || t.status === filterStatus;
        const mt = filterType === 'all' || t.type === filterType;
        const mq = !searchQ || t.title.toLowerCase().includes(searchQ.toLowerCase()) || t.location.toLowerCase().includes(searchQ.toLowerCase());
        // Client-side date range filter (in addition to the API filter)
        const afterStart = !filterStartDate || t.date >= filterStartDate;
        const beforeEnd = !filterEndDate || t.date <= filterEndDate;
        return ms && mt && mq && afterStart && beforeEnd;
    });

    // ── Stats ───────────────────────────────────────────────────────────────
    const stats = {
        total: tasks.length,
        completed: tasks.filter(t => t.status === 'completed').length,
        overdue: tasks.filter(t => t.status === 'overdue').length,
        scheduled: tasks.filter(t => t.status === 'scheduled').length,
    };
    const completionRate = stats.total > 0 ? Math.round((stats.completed / stats.total) * 100) : 0;

    // ── Export CSV ───────────────────────────────────────────────────────────
    const exportCSV = () => {
        const headers = ['Title', 'Type', 'Date', 'Start Time', 'End Time', 'Assigned To', 'Location', 'Priority', 'Status', 'Recurring', 'Pattern', 'Description'];
        const rows = filtered.map(t => [
            t.title,
            TYPE_CONFIG[t.type]?.label || t.type,
            t.date,
            t.startTime,
            t.endTime,
            t.assignedTo,
            t.location,
            t.priority,
            t.status,
            t.isRecurring ? 'Yes' : 'No',
            t.recurringPattern || '',
            (t.description || '').replace(/\n/g, ' '),
        ]);
        const csv = [headers, ...rows]
            .map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','))
            .join('\n');
        const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `PM_Schedule_${site}_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    // ── CRUD ─────────────────────────────────────────────────────────────────
    const openCreate = (date?: string) => {
        setEditTask(null);
        setEquipSearch('');
        setShowEquipDropdown(false);
        setForm({ ...DEFAULT_FORM, date: date || fmtDate(new Date()) });
        setShowModal(true);
    };

    const openEdit = (task: PMTask) => {
        setEditTask(task);
        setEquipSearch('');
        setShowEquipDropdown(false);
        setForm({ title: task.title, type: task.type, date: task.date, startTime: task.startTime, endTime: task.endTime, assignedTo: task.assignedTo, location: task.location, description: task.description || '', priority: task.priority, isRecurring: task.isRecurring, recurringPattern: task.recurringPattern || '', equipmentNames: Array.isArray(task.equipmentNames) ? task.equipmentNames : [] });
        setSelectedTask(null);
        setShowModal(true);
    };

    const saveTask = async () => {
        if (!form.title || !form.date) return;
        const cfg = TYPE_CONFIG[form.type];

        // Prepare task data for backend - mapping camelCase to snake_case
        const taskData: any = {
            title: form.title,
            type: form.type,
            date: form.date,
            start_time: form.startTime,
            end_time: form.endTime,
            assigned_to: form.assignedTo,
            location: form.location,
            description: form.description,
            priority: form.priority,
            is_recurring: form.isRecurring,
            recurring_pattern: form.recurringPattern,
            status: editTask?.status || 'scheduled',
            site_code: site,
            site_id: localStorage.getItem('site_id') || sessionStorage.getItem('site_id'),
            company_id: localStorage.getItem('company_id') || sessionStorage.getItem('company_id'),
            equipment_names: form.equipmentNames,
        };

        if (editTask) {
            taskData.id = editTask.id;
        }

        try {
            const res = await (backendApi as any).savePMCalendarEvent?.(taskData);
            if (res?.success) {
                // Always refresh from backend to avoid response-shape mismatches
                // that can hide newly created activities from date cells.
                await loadTasks();
                // Move calendar focus to selected task month/date after save.
                const [yy, mm, dd] = String(form.date).split('-').map(Number);
                if (yy && mm && dd) {
                    setCurrentDate(new Date(yy, mm - 1, dd));
                }
            }
        } catch (error) {
            console.error('Error saving task:', error);
            // Fallback for demo if API fails
            if (!editTask) {
                const fallbackTask: PMTask = {
                    id: Date.now().toString(),
                    ...form,
                    status: 'scheduled',
                    color: cfg.color,
                    site,
                };
                setTasks(prev => [...prev, fallbackTask]);
            }
        }

        setShowModal(false);
        setForm(DEFAULT_FORM);
        setEditTask(null);
    };

    const deleteTask = async (id: string) => {
        setTasks(prev => prev.filter(t => t.id !== id));
        setSelectedTask(null);
        try { await (backendApi as any).deletePMCalendarEvent?.(id); } catch { }
    };

    const updateStatus = async (id: string, status: TaskStatus, instanceDate?: string) => {
        // For recurring tasks, only update the specific instance
        if (instanceDate && selectedTask?.isRecurring) {
            const instanceId = getInstanceId(id, instanceDate);
            setInstanceStatuses(prev => ({ ...prev, [instanceId]: status }));
            if (selectedTask?.id === id) setSelectedTask(prev => prev ? { ...prev, status } : prev);
            
            // Notify backend about this specific instance update
            try {
                await (backendApi as any).updatePMCalendarStatus?.(id, status, { instance_date: instanceDate });
            } catch (error) {
                console.error('Error updating instance status:', error);
            }
        } else {
            // For non-recurring tasks, update normally
            setTasks(prev => prev.map(t => t.id === id ? { ...t, status } : t));
            if (selectedTask?.id === id) setSelectedTask(prev => prev ? { ...prev, status } : prev);
            try { await (backendApi as any).updatePMCalendarStatus?.(id, status); } catch (error) {
                console.error('Error updating task status:', error);
            }
        }
    };

    const connectTaskToPTWFlow = async () => {
        if (!selectedTask || !site) return;
        setFlowError('');
        setFlowSuccess(null);
        setFlowLoading(true);

        const payload = {
            site_code: site,
            source_instance_date: selectedInstanceDate || selectedTask.date,
            is_hv_involved: selectedTask.priority === 'critical',
        };

        try {
            const res = await (backendApi as any).connectPMTaskToPTWFlow?.(selectedTask.id, payload);
            if (res?.success) {
                setFlowSuccess(res);
            } else {
                setFlowError(res?.error || 'Unable to connect PM task with PTW flow right now.');
            }
        } catch (error: any) {
            setFlowError(error?.message || 'Unable to connect PM task with PTW flow right now.');
        } finally {
            setFlowLoading(false);
        }
    };

    useEffect(() => {
        setFlowError('');
        setFlowSuccess(null);
    }, [selectedTask?.id, selectedInstanceDate]);

    // ── Navigation ───────────────────────────────────────────────────────────
    const navigate = (dir: 1 | -1) => {
        setCurrentDate(prev => {
            const d = new Date(prev);
            if (view === 'month') d.setMonth(d.getMonth() + dir);
            else if (view === 'week') d.setDate(d.getDate() + dir * 7);
            else d.setDate(d.getDate() + dir);
            return d;
        });
    };

    const headerLabel = () => {
        if (view === 'month') return `${MONTH_NAMES[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
        if (view === 'week') {
            const wd = weekDays();
            return `${MONTH_NAMES[wd[0].getMonth()]} ${wd[0].getDate()} – ${wd[6].getDate()}, ${wd[6].getFullYear()}`;
        }
        return currentDate.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    };

    // ─────────────────────────────────────────────────────────────────────────
    return (
        <div className="om-page omx h-full flex flex-col">
            <div className="flex flex-col h-full" style={{ background: '#FFFFFF' }}>

                {/* ── Page header ──────────────────────────────────────── */}
                <div className="omx-shell" style={{ flex: 'none' }}>
                    <div className="omx-pagehead" style={{ marginBottom: 0 }}>
                        <div style={{ minWidth: 0 }}>
                            <div className="omx-crumbs">
                                <span>Operations &amp; Maintenance</span>
                                <ChevronRight />
                                <b>PM Calendar</b>
                            </div>
                            <span className="om-eyebrow">{siteDisplayName} &middot; Preventive Maintenance</span>
                            <h1 className="om-h1" style={{ marginTop: 10 }}>PM Calendar</h1>
                        </div>

                        <div className="omx-pagehead__actions">
                            <div className="omx-field">
                                <Search size={13} strokeWidth={2} style={{ color: '#6B7A8B', flex: 'none' }} />
                                <input
                                    value={searchQ}
                                    onChange={e => setSearchQ(e.target.value)}
                                    placeholder="Search tasks…"
                                    aria-label="Search tasks"
                                    style={{ width: 130 }}
                                />
                            </div>

                            <div className="omx-field">
                                <select value={filterType} onChange={e => setFilterType(e.target.value)} aria-label="Filter by type">
                                    <option value="all">All Types</option>
                                    {Object.entries(TYPE_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                                </select>
                            </div>

                            <div className="omx-field">
                                <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} aria-label="Filter by status">
                                    <option value="all">All Status</option>
                                    {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                                </select>
                            </div>

                            <button
                                onClick={() => setShowDateRangeFilter(p => !p)}
                                className={`omx-btn${(filterStartDate || filterEndDate) ? ' omx-btn--primary' : ''}`}
                                aria-pressed={!!(filterStartDate || filterEndDate)}
                            >
                                <Filter size={13} strokeWidth={1.9} /> Range
                            </button>

                            <button onClick={exportCSV} className="omx-btn">
                                <Download size={13} strokeWidth={1.9} /> Export
                            </button>

                            <button
                                onClick={() => { setFilterStartDate(''); setFilterEndDate(''); setFilterType('all'); setFilterStatus('all'); setSearchQ(''); }}
                                className="omx-btn"
                                title="Reset filters"
                                aria-label="Reset filters"
                                style={{ padding: '8px 10px' }}
                            >
                                <RefreshCw size={13} strokeWidth={1.9} />
                            </button>

                            <button onClick={() => openCreate()} className="omx-btn omx-btn--primary">
                                <Plus size={13} strokeWidth={2} /> New Task
                            </button>
                        </div>
                    </div>
                </div>

                {/* ── Key figures ──────────────────────────────────────── */}
                <div className="omx-shell" style={{ flex: 'none', paddingBlock: 'var(--om-gap)' }}>
                    <div className="omx-kpis">
                        <div className="omx-kpi">
                            <span className="omx-kpi__label">Total Tasks</span>
                            <span className="omx-kpi__value">{stats.total}</span>
                        </div>
                        <div className="omx-kpi">
                            <span className="omx-kpi__label">Completed</span>
                            <span className="omx-kpi__value" style={{ color: '#1E7F52' }}>{stats.completed}</span>
                        </div>
                        <div className="omx-kpi">
                            <span className="omx-kpi__label">Scheduled</span>
                            <span className="omx-kpi__value">{stats.scheduled}</span>
                        </div>
                        <div className="omx-kpi">
                            <span className="omx-kpi__label">Overdue</span>
                            <span className="omx-kpi__value" style={{ color: stats.overdue > 0 ? '#B3261E' : undefined }}>{stats.overdue}</span>
                        </div>
                        <div className="omx-kpi">
                            <span className="omx-kpi__label">Completion Rate</span>
                            <span className="omx-kpi__value">
                                {completionRate}<span className="omx-kpi__unit">%</span>
                            </span>
                            <div style={{ marginTop: 9, height: 3, borderRadius: 2, background: '#EEF2F7', overflow: 'hidden' }}>
                                <div style={{ height: '100%', width: `${completionRate}%`, background: '#0084CF', transition: 'width .5s ease' }} />
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Date range filter ────────────────────────────────── */}
                <AnimatePresence>
                    {showDateRangeFilter && (
                        <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            style={{ overflow: 'hidden', flex: 'none' }}
                        >
                            <div className="omx-shell" style={{ paddingBottom: 'var(--om-gap)' }}>
                                <div
                                    className="omx-card"
                                    style={{ flexDirection: 'row', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '12px 16px' }}
                                >
                                    <span className="om-label">Date Range</span>
                                    <div className="omx-field">
                                        <input type="date" value={filterStartDate} onChange={e => setFilterStartDate(e.target.value)} aria-label="Start date" />
                                    </div>
                                    <span className="om-body" style={{ fontSize: 12 }}>to</span>
                                    <div className="omx-field">
                                        <input type="date" value={filterEndDate} onChange={e => setFilterEndDate(e.target.value)} aria-label="End date" />
                                    </div>
                                    {(filterStartDate || filterEndDate) && (
                                        <button onClick={() => { setFilterStartDate(''); setFilterEndDate(''); }} className="omx-btn">
                                            Clear
                                        </button>
                                    )}
                                    <span className="om-label" style={{ marginLeft: 'auto' }}>
                                        {filtered.length} tasks in range
                                    </span>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* ── Calendar toolbar ─────────────────────────────────── */}
                <div className="omx-shell" style={{ flex: 'none' }}>
                    <div className="omx-cal__bar">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                            <button onClick={() => navigate(-1)} className="omx-iconbtn" aria-label="Previous period">
                                <ChevronLeft size={15} strokeWidth={2} />
                            </button>
                            <button onClick={() => setCurrentDate(new Date())} className="omx-btn" style={{ padding: '7px 13px' }}>
                                Today
                            </button>
                            <button onClick={() => navigate(1)} className="omx-iconbtn" aria-label="Next period">
                                <ChevronRight size={15} strokeWidth={2} />
                            </button>

                            <div className="relative" style={{ marginLeft: 6 }}>
                                <button
                                    onClick={() => setShowDatePicker(!showDatePicker)}
                                    className="omx-cal__period"
                                    aria-expanded={showDatePicker}
                                >
                                    {headerLabel()}
                                    <ChevronDown size={13} strokeWidth={2} style={{ opacity: .7 }} />
                                </button>
                                {showDatePicker && (
                                    <div className="omx-popover">
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                                            <button onClick={() => setPickerYear(y => y - 1)} className="omx-iconbtn" aria-label="Previous year">
                                                <ChevronLeft size={13} strokeWidth={2} />
                                            </button>
                                            <span className="om-h2">{pickerYear}</span>
                                            <button onClick={() => setPickerYear(y => y + 1)} className="omx-iconbtn" aria-label="Next year">
                                                <ChevronRight size={13} strokeWidth={2} />
                                            </button>
                                        </div>
                                        <div className="grid grid-cols-4 gap-1">
                                            {MONTH_NAMES.map((mn, i) => {
                                                const on = currentDate.getMonth() === i && currentDate.getFullYear() === pickerYear;
                                                return (
                                                    <button
                                                        key={mn}
                                                        onClick={() => { setCurrentDate(new Date(pickerYear, i, 1)); setShowDatePicker(false); }}
                                                        className="omx-seg__btn"
                                                        aria-pressed={on}
                                                        style={{ justifyContent: 'center', padding: '7px 0' }}
                                                    >
                                                        {mn.slice(0, 3)}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="omx-seg" role="group" aria-label="Calendar view">
                            {(['Month', 'Week', 'Day'] as const).map(v => (
                                <button
                                    key={v}
                                    onClick={() => { setView(v.toLowerCase() as CalView); setActiveView(v); }}
                                    className="omx-seg__btn"
                                    aria-pressed={activeView === v}
                                >
                                    {v}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* ─ Calendar Content ─ */}
                <div className="flex-1 overflow-auto">
                    {loading && (
                        <div className="flex items-center justify-center h-40 text-gray-400 text-sm gap-2">
                            <RefreshCw size={16} className="animate-spin" /> Loading tasks…
                        </div>
                    )}

                    {/* MONTH VIEW */}
                    {!loading && view === 'month' && (
                        <div>
                            {/* Day headers */}
                            <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50">
                                {DAY_SHORT.map(d => (
                                    <div key={d} className="text-center py-2 text-[11px] font-black text-gray-900 tracking-widest uppercase" style={{ fontWeight: 900, WebkitTextStroke: '0.6px currentColor' }}>{d}</div>
                                ))}
                            </div>
                            {/* Grid */}
                            <div className="grid grid-cols-7">
                                {calDays().map((day, i) => {
                                    const dayTasks = day ? tasksForDate(day) : [];
                                    const isCurrentDay = day && isToday(day);
                                    return (
                                        <div key={i}
                                            onClick={() => day && openCreate(fmtDate(day))}
                                            className={`border-r border-b border-gray-100 min-h-[110px] p-1.5 cursor-pointer transition-colors ${day ? 'hover:bg-[#F2F8FC]/30' : 'bg-gray-50/40'} ${!day ? 'cursor-default' : ''}`}>
                                            {day && (
                                                <>
                                                    <div className="flex items-center justify-start mb-1">
                                                        {isCurrentDay ? (
                                                            <div className="w-6 h-6 bg-[#0084CF] text-white rounded-full flex items-center justify-center text-xs">{day.getDate()}</div>
                                                        ) : (
                                                            <span className="text-xs text-gray-500 px-1">{day.getDate()}</span>
                                                        )}
                                                    </div>
                                                    <div className="space-y-0.5">
                                                        {dayTasks.slice(0, 3).map(task => {
                                                            const cfg = TYPE_CONFIG[task.type];
                                                            return (
                                                                <motion.div key={task.id} whileHover={{ scale: 1.01 }}
                                                                    onClick={e => { e.stopPropagation(); setSelectedTask(task); setSelectedInstanceDate(day ? fmtDate(day) : null); }}
                                                                    className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-black cursor-pointer truncate"
                                                                    style={{ backgroundColor: cfg.color + '18', borderLeft: `3px solid ${cfg.color}`, color: cfg.color }}>
                                                                    <span className="flex-shrink-0">{cfg.icon}</span>
                                                                    <span className="truncate">{task.title}</span>
                                                                </motion.div>
                                                            );
                                                        })}
                                                        {dayTasks.length > 3 && (
                                                            <div className="text-[9px] text-gray-400 pl-1">+{dayTasks.length - 3} more</div>
                                                        )}
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* WEEK VIEW */}
                    {!loading && view === 'week' && (
                        <div>
                            <div className="grid grid-cols-8 border-b border-gray-200 bg-gray-50 sticky top-0 z-10">
                                <div className="py-2 text-center text-[10px] text-gray-400 border-r border-gray-200">TIME</div>
                                {weekDays().map((day, i) => (
                                    <div key={i} className="py-2 text-center border-r border-gray-200 last:border-r-0">
                                        <div className="text-[11px] font-black text-gray-900 tracking-wider uppercase" style={{ fontWeight: 900, WebkitTextStroke: '0.6px currentColor' }}>{DAY_SHORT[day.getDay()]}</div>
                                        <div className={`text-lg mx-auto w-8 h-8 flex items-center justify-center rounded-full ${isToday(day) ? 'bg-[#0084CF] text-white' : 'text-gray-800'}`}>
                                            {day.getDate()}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 280px)' }}>
                                {Array.from({ length: 13 }, (_, i) => i + 6).map(hour => (
                                    <div key={hour} className="grid grid-cols-8 border-b border-gray-100 min-h-[60px]">
                                        <div className="border-r border-gray-200 px-2 py-1 text-[10px] font-black text-gray-700 text-right" style={{ fontWeight: 900, WebkitTextStroke: '0.3px currentColor' }}>{hour}:00</div>
                                        {weekDays().map((day, di) => {
                                            const dayTasks = tasksForDate(day).filter(t => parseInt(t.startTime.split(':')[0]) === hour);
                                            return (
                                                <div key={di} className="border-r border-gray-100 last:border-r-0 p-1 hover:bg-[#F2F8FC]/20 cursor-pointer"
                                                    onClick={() => openCreate(fmtDate(day))}>
                                                    {dayTasks.map(task => {
                                                        const cfg = TYPE_CONFIG[task.type];
                                                        return (
                                                            <div key={task.id} onClick={e => { e.stopPropagation(); setSelectedTask(task); setSelectedInstanceDate(fmtDate(day)); }}
                                                                className="text-[10px] px-1 py-0.5 rounded mb-0.5 cursor-pointer font-bold truncate"
                                                                style={{ backgroundColor: cfg.color + '20', color: cfg.color, borderLeft: `2px solid ${cfg.color}` }}>
                                                                {task.title}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            );
                                        })}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* DAY VIEW */}
                    {!loading && view === 'day' && (
                        <div>
                            <div className="text-center py-3 border-b border-gray-200 bg-gray-50">
                                <div className="text-[12px] font-black text-gray-900 uppercase tracking-widest" style={{ fontWeight: 900, WebkitTextStroke: '0.6px currentColor' }}>{DAY_SHORT[currentDate.getDay()]}</div>
                                <div className={`text-2xl mx-auto w-12 h-12 flex items-center justify-center rounded-full mt-1 ${isToday(currentDate) ? 'bg-[#0084CF] text-white' : 'text-gray-800'}`}>
                                    {currentDate.getDate()}
                                </div>
                            </div>
                            <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 280px)' }}>
                                {Array.from({ length: 15 }, (_, i) => i + 6).map(hour => {
                                    const dayTasks = tasksForDate(currentDate).filter(t => parseInt(t.startTime.split(':')[0]) === hour);
                                    return (
                                        <div key={hour} className="flex gap-4 border-b border-gray-100 min-h-[70px] px-4 py-2 hover:bg-[#F2F8FC]/10">
                                            <div className="w-16 text-right text-xs font-bold text-gray-400 pt-1 flex-shrink-0">{hour}:00</div>
                                            <div className="flex-1 space-y-1">
                                                {dayTasks.map(task => {
                                                    const cfg = TYPE_CONFIG[task.type];
                                                    const dayStr = fmtDate(currentDate);
                                                    const instanceStatus = getInstanceStatus(task, dayStr);
                                                    const sCfg = STATUS_CONFIG[instanceStatus];
                                                    return (
                                                        <motion.div key={task.id} whileHover={{ x: 3 }}
                                                            onClick={() => { setSelectedTask(task); setSelectedInstanceDate(fmtDate(currentDate)); }}
                                                            className="flex items-center gap-3 p-2.5 rounded-xl cursor-pointer"
                                                            style={{ backgroundColor: cfg.color + '12', borderLeft: `4px solid ${cfg.color}` }}>
                                                            <div className="flex-1 min-w-0">
                                                                <div className="flex items-center gap-2">
                                                                    <span className={cfg.text}>{cfg.icon}</span>
                                                                    <span className="text-sm font-bold text-gray-900 truncate">{task.title}</span>
                                                                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${sCfg.badge}`}>{sCfg.label}</span>
                                                                </div>
                                                                <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500">
                                                                    <span className="flex items-center gap-1"><Clock size={10} />{task.startTime}–{task.endTime}</span>
                                                                    <span className="flex items-center gap-1"><MapPin size={10} />{task.location}</span>
                                                                    <span className="flex items-center gap-1"><Users size={10} />{task.assignedTo}</span>
                                                                </div>
                                                            </div>
                                                        </motion.div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>

                {/* ─ Task Detail Panel ─ */}
                <AnimatePresence>
                    {selectedTask && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100000] flex items-center justify-center p-4"
                            onClick={() => setSelectedTask(null)}>
                            <motion.div initial={{ scale: 0.92, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.92, opacity: 0, y: 20 }}
                                onClick={e => e.stopPropagation()}
                                className="bg-white rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden"
                                style={{ zIndex: 10000 }}>
                                {/* Header */}
                                <div className="p-6 pb-2 flex items-start gap-4 bg-white">
                                    <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: TYPE_CONFIG[selectedTask.type].color + '20' }}>
                                        <span style={{ color: TYPE_CONFIG[selectedTask.type].color }}>{TYPE_CONFIG[selectedTask.type].icon}</span>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap mb-1">
                                            {(() => {
                                                const instanceStatus = getInstanceStatus(selectedTask, selectedInstanceDate || selectedTask.date);
                                                const statusCfg = STATUS_CONFIG[instanceStatus];
                                                return (
                                                    <span className={`text-xs px-2 py-0.5 rounded-full border ${statusCfg.badge}`}>
                                                        <span className={`w-1.5 h-1.5 rounded-full ${statusCfg.dot} inline-block mr-1`}></span>
                                                        {statusCfg.label}
                                                    </span>
                                                );
                                            })()}
                                            <span className={`text-xs ${TYPE_CONFIG[selectedTask.type].text}`}>{TYPE_CONFIG[selectedTask.type].label}</span>
                                        </div>
                                        <h3 className="text-gray-900 text-lg font-black flex items-center gap-2" style={{ fontWeight: 900, WebkitTextStroke: '0.5px currentColor' }}>
                                            {selectedTask.wo_number && (
                                                <span className="text-xs bg-[#E3F0F9] text-[#1C4D88] border border-[#C2DFF1] px-2 py-0.5 rounded font-mono font-bold">{selectedTask.wo_number}</span>
                                            )}
                                            <span>{selectedTask.title}</span>
                                        </h3>
                                    </div>
                                    <button onClick={() => setSelectedTask(null)} className="p-1.5 hover:bg-gray-100 rounded-lg">
                                        <X size={16} className="text-gray-500" />
                                    </button>
                                </div>
                                {/* Body */}
                                <div className="p-4 space-y-3">
                                    <div className="grid grid-cols-2 gap-3">
                                        {[
                                            { icon: <CalendarIcon size={15} />, label: 'Date', val: selectedInstanceDate || selectedTask.date },
                                            { icon: <Clock size={15} />, label: 'Time', val: `${selectedTask.startTime} – ${selectedTask.endTime}` },
                                            { icon: <MapPin size={15} />, label: 'Location', val: selectedTask.location },
                                            { icon: <Users size={15} />, label: 'Assigned', val: selectedTask.assignedTo },
                                        ].map(item => (
                                            <div key={item.label} className="flex items-start gap-2 bg-gray-50 rounded-xl p-2.5">
                                                <span className="text-gray-400 mt-0.5 flex-shrink-0">{item.icon}</span>
                                                <div>
                                                    <div className="text-[10px] font-black text-gray-400 uppercase tracking-wider" style={{ fontWeight: 900, WebkitTextStroke: '0.3px currentColor' }}>{item.label}</div>
                                                    <div className="text-sm font-black text-gray-800" style={{ fontWeight: 900, WebkitTextStroke: '0.4px currentColor' }}>{item.val}</div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    {selectedTask.description && (
                                        <div className="bg-gray-50 rounded-xl p-3">
                                            <div className="text-[10px] text-gray-400 mb-1">Description</div>
                                            <p className="text-sm text-gray-700">{selectedTask.description}</p>
                                        </div>
                                    )}
                                    {selectedTask.isRecurring && (
                                        <div className="flex items-center gap-2 text-sm text-[#0066A3] bg-[#F2F8FC] rounded-xl p-3">
                                            <Repeat size={14} /> <span>{selectedTask.recurringPattern}</span>
                                        </div>
                                    )}
                                    {Array.isArray(selectedTask.equipmentNames) && selectedTask.equipmentNames.length > 0 && (
                                        <div className="bg-gray-50 rounded-xl p-3">
                                            <div className="text-[10px] text-gray-400 mb-1.5">Equipment Assigned</div>
                                            <div className="flex flex-wrap gap-1.5">
                                                {selectedTask.equipmentNames.map(eq => (
                                                    <span key={eq} className="inline-flex items-center bg-[#F2F8FC] border border-[#C2DFF1] text-[#1C4D88] rounded-full px-2.5 py-0.5 text-xs font-semibold">
                                                        <Wrench size={10} className="mr-1 flex-shrink-0" />{eq}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    <div className="bg-gray-50 rounded-xl p-3 border border-gray-200">
                                        <div className="flex items-center justify-between gap-2 mb-2">
                                            <div className="flex items-center gap-2">
                                                <Bell size={14} className="text-[#0084CF]" />
                                                <p className="text-xs font-black text-gray-700 uppercase tracking-wide" style={{ fontWeight: 900 }}>PTW Flow</p>
                                            </div>
                                            <span className={`text-[10px] px-2 py-0.5 rounded-full border ${flowSuccess ? 'bg-green-50 text-green-700 border-green-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                                                {flowSuccess ? 'Connected' : 'Not Connected'}
                                            </span>
                                        </div>
                                        <div className="grid grid-cols-2 gap-2 text-[11px] text-gray-600">
                                            <div>1. Work Order Created</div>
                                            <div>2. PTW Draft Prepared</div>
                                            <div>3. Review & Approval</div>
                                            <div>4. Execution & Closure</div>
                                        </div>
                                        <div className="mt-2 text-[11px] text-gray-500">
                                            Generate the PTW flow once, then continue work lifecycle from PM status actions.
                                        </div>
                                        {flowSuccess && (
                                            <div className="mt-3 text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg px-2 py-1.5 space-y-1">
                                                <div className="font-semibold">Flow connected successfully</div>
                                                <div>Work Order: {flowSuccess.work_order_no || 'Generated'}</div>
                                                <div>PTW: {flowSuccess.permit_no || 'Generated'}</div>
                                            </div>
                                        )}
                                        {flowError && (
                                            <div className="mt-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-2 py-1.5">
                                                {flowError}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="omx-modal__foot">
                                    <button onClick={() => setShowModal(false)} className="omx-btn">
                                        Cancel
                                    </button>
                                    <button
                                        onClick={saveTask}
                                        disabled={!form.title || !form.date}
                                        className="omx-btn omx-btn--primary"
                                    >
                                        {editTask ? 'Update Task' : 'Create Task'}
                                    </button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* ─ Create / Edit Modal ─ */}
                <AnimatePresence>
                    {showModal && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            className="omx-overlay"
                            onClick={() => setShowModal(false)}>
                            <motion.div initial={{ scale: 0.97, opacity: 0, y: 12 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.97, opacity: 0, y: 12 }}
                                onClick={e => e.stopPropagation()}
                                role="dialog"
                                aria-modal="true"
                                aria-label={editTask ? 'Edit PM Task' : 'New PM Task'}
                                className="omx-modal"
                                style={{ zIndex: 10001, colorScheme: 'light', overflow: 'visible' }}>

                                <div className="omx-modal__head">
                                    <div style={{ minWidth: 0 }}>
                                        <span className="omx-modal__eyebrow">Preventive Maintenance</span>
                                        <div className="omx-modal__title">{editTask ? 'Edit PM Task' : 'New PM Task'}</div>
                                        <p className="omx-modal__sub">
                                            {editTask
                                                ? 'Update the schedule, assignment and scope of this task.'
                                                : 'Schedule a maintenance activity and assign it to the site team.'}
                                        </p>
                                    </div>
                                    <button onClick={() => setShowModal(false)} className="omx-modal__close" aria-label="Close">
                                        <X size={17} strokeWidth={1.9} />
                                    </button>
                                </div>
                                {/* Form */}
                                <div className="omx-modal__body omx-formgrid">
                                    {conflictWarning && (
                                        <div className="omx-notice omx-notice--warn">
                                            <AlertTriangle size={15} strokeWidth={1.9} />
                                            <span>{conflictWarning}</span>
                                        </div>
                                    )}
                                    {/* Title */}
                                    <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
                                        placeholder="Task title…"
                                        className="w-full border border-gray-200 rounded-xl px-4 py-2 text-sm font-semibold text-black outline-none focus:ring-2 focus:ring-[#0084CF]/30 placeholder-gray-400"
                                        style={{ fontWeight: 600, color: 'black' }} />

                                    {/* Type */}
                                    <div>
                                        <label className="text-[11px] font-black text-gray-700 uppercase mb-1 block" style={{ fontWeight: 900 }}>Activity Type</label>
                                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                            {Object.entries(TYPE_CONFIG).map(([k, v]) => (
                                                <button
                                                    key={k}
                                                    type="button"
                                                    onClick={() => setForm({ ...form, type: k as TaskType })}
                                                    className="omx-choice"
                                                    aria-pressed={form.type === k}
                                                >
                                                    <span className="omx-choice__mark" style={{ color: v.color }}>{v.icon}</span>
                                                    <span>{v.label}</span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Date + Time */}
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                        <div>
                                            <label className="text-[11px] font-black text-gray-700 uppercase mb-1 block" style={{ fontWeight: 900 }}>Date</label>
                                            <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })}
                                                className="w-full border border-gray-200 rounded-xl px-2.5 py-2 text-sm text-black font-semibold outline-none focus:ring-2 focus:ring-[#0084CF]/30"
                                                style={{ fontWeight: 600, color: 'black' }} />
                                        </div>
                                        <div>
                                            <label className="text-[11px] font-black text-gray-700 uppercase mb-1 block" style={{ fontWeight: 900 }}>Start</label>
                                            <input type="time" value={form.startTime} onChange={e => setForm({ ...form, startTime: e.target.value })}
                                                className="w-full border border-gray-200 rounded-xl px-2.5 py-1.5 text-sm font-semibold text-black outline-none focus:ring-2 focus:ring-[#0084CF]/30"
                                                style={{ fontWeight: 600, color: 'black' }} />
                                        </div>
                                        <div>
                                            <label className="text-[11px] font-black text-gray-700 uppercase mb-1 block" style={{ fontWeight: 900 }}>End</label>
                                            <input type="time" value={form.endTime} onChange={e => setForm({ ...form, endTime: e.target.value })}
                                                className="w-full border border-gray-200 rounded-xl px-2.5 py-1.5 text-sm font-semibold text-black outline-none focus:ring-2 focus:ring-[#0084CF]/30"
                                                style={{ fontWeight: 600, color: 'black' }} />
                                        </div>
                                    </div>

                                    {/* Assigned + Location */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        <div>
                                            <label className="text-[11px] font-black text-gray-700 uppercase mb-1 block" style={{ fontWeight: 900 }}>Assigned To</label>
                                            <div className="space-y-1.5">
                                                <select
                                                    value={assignableUsers.find(u => u.displayName === form.assignedTo || u.username === form.assignedTo || u.id === form.assignedTo)?.id || ''}
                                                    onChange={e => {
                                                        const selected = assignableUsers.find(u => u.id === e.target.value);
                                                        setForm({ ...form, assignedTo: selected ? selected.displayName : '' });
                                                    }}
                                                    className="w-full border border-gray-200 rounded-xl px-3 py-1.5 text-sm text-black font-semibold outline-none focus:ring-2 focus:ring-[#0084CF]/30 bg-white"
                                                    style={{ fontWeight: 600, color: 'black' }}
                                                >
                                                    <option value="">{usersLoading ? 'Loading site users...' : 'Select site user'}</option>
                                                    {assignableUsers.map(user => (
                                                        <option key={user.id} value={user.id}>
                                                            {user.displayName}{user.username && user.displayName !== user.username ? ` (${user.username})` : ''}
                                                        </option>
                                                    ))}
                                                </select>
                                                <input
                                                    value={form.assignedTo}
                                                    onChange={e => setForm({ ...form, assignedTo: e.target.value })}
                                                    placeholder="Or enter custom name / team"
                                                    className="w-full border border-gray-200 rounded-xl px-3 py-1.5 text-sm font-semibold text-black outline-none focus:ring-2 focus:ring-[#0084CF]/30"
                                                    style={{ fontWeight: 600, color: 'black' }}
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="text-[11px] font-black text-gray-700 uppercase mb-1 block" style={{ fontWeight: 900 }}>Location</label>
                                            <input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })}
                                                placeholder="Block / Zone"
                                                className="w-full border border-gray-200 rounded-xl px-3 py-1.5 text-sm font-semibold text-black outline-none focus:ring-2 focus:ring-[#0084CF]/30"
                                                style={{ fontWeight: 600, color: 'black' }} />
                                        </div>
                                        <div className="col-span-2">
                                            <div className="flex items-center justify-between mb-1">
                                                <label className="text-[11px] font-black text-gray-700 uppercase" style={{ fontWeight: 900 }}>
                                                    Equipment <span className="normal-case text-gray-400 font-normal">(from Inventory)</span>
                                                </label>
                                                {form.equipmentNames.length > 0 && (
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-[10px] bg-[#0084CF] text-white rounded-full px-2 py-0.5 font-bold">{form.equipmentNames.length} selected</span>
                                                        <button type="button"
                                                            onClick={() => setForm({ ...form, equipmentNames: [] })}
                                                            className="text-[10px] text-red-400 hover:text-red-600 transition-colors font-semibold">
                                                            Clear all
                                                        </button>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Chip + search box */}
                                            <div
                                                ref={equipmentDropdownRef}
                                                className={`relative border rounded-xl bg-white transition-all ${showEquipDropdown ? 'border-[#0084CF] ring-2 ring-[#0084CF]/20' : 'border-gray-200 hover:border-gray-300'}`}
                                                onClick={() => setShowEquipDropdown(true)}
                                            >
                                                <div className="flex flex-wrap gap-1.5 px-2.5 pt-2 pb-1.5 min-h-[42px] cursor-text">
                                                    {form.equipmentNames.map(eq => (
                                                        <span key={eq}
                                                            className="inline-flex items-center gap-1 bg-[#e8f4ff] border border-[#b3d9f7] text-[#0065a0] rounded-lg px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap max-w-[180px]"
                                                            title={eq}>
                                                            <Wrench size={9} className="flex-shrink-0 opacity-60" />
                                                            <span className="truncate">{eq}</span>
                                                            <button
                                                                type="button"
                                                                onClick={e => { e.stopPropagation(); setForm({ ...form, equipmentNames: form.equipmentNames.filter(n => n !== eq) }); }}
                                                                className="flex-shrink-0 ml-0.5 w-3.5 h-3.5 rounded-full hover:bg-red-100 hover:text-red-500 flex items-center justify-center transition-colors">
                                                                <X size={9} />
                                                            </button>
                                                        </span>
                                                    ))}
                                                    {/* Inline search input */}
                                                    <div className="flex items-center gap-1 flex-1 min-w-[120px]">
                                                        <Search size={12} className="text-gray-300 flex-shrink-0" />
                                                        <input
                                                            value={equipSearch}
                                                            onChange={e => { setEquipSearch(e.target.value); setShowEquipDropdown(true); }}
                                                            onFocus={() => setShowEquipDropdown(true)}
                                                            placeholder={equipsLoading ? 'Loading…' : form.equipmentNames.length === 0 ? 'Search equipment from inventory…' : 'Add more…'}
                                                            className="flex-1 bg-transparent border-none outline-none text-xs text-black placeholder-gray-300 py-0.5"
                                                            style={{ minWidth: 80 }}
                                                        />
                                                        {equipSearch && (
                                                            <button type="button"
                                                                onClick={e => { e.stopPropagation(); setEquipSearch(''); }}
                                                                className="text-gray-300 hover:text-gray-500 flex-shrink-0"><X size={12} /></button>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Dropdown list */}
                                                {showEquipDropdown && (
                                                    <div className="relative z-[200] left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-2xl overflow-hidden"
                                                        style={{ maxHeight: 200 }}>
                                                        <div className="overflow-y-auto" style={{ maxHeight: 200 }}>
                                                            {(() => {
                                                                const filtered = inventoryEquipments
                                                                    .filter(eq => !equipSearch || eq.toLowerCase().includes(equipSearch.toLowerCase()))
                                                                    .filter(eq => !form.equipmentNames.includes(eq));
                                                                const showAdd = equipSearch.trim() &&
                                                                    !inventoryEquipments.some(eq => eq.toLowerCase() === equipSearch.trim().toLowerCase()) &&
                                                                    !form.equipmentNames.includes(equipSearch.trim());

                                                                if (filtered.length === 0 && !showAdd) {
                                                                    return (
                                                                        <div className="flex flex-col items-center justify-center py-5 text-gray-400">
                                                                            <Wrench size={18} className="mb-1.5 opacity-30" />
                                                                            <span className="text-xs">
                                                                                {equipSearch ? 'No match found' : 'No equipment in inventory for this site'}
                                                                            </span>
                                                                        </div>
                                                                    );
                                                                }
                                                                return (
                                                                    <>
                                                                        {filtered.map((eq, idx) => (
                                                                            <button key={eq} type="button"
                                                                                onClick={e => { e.stopPropagation(); setForm({ ...form, equipmentNames: [...form.equipmentNames, eq] }); setEquipSearch(''); }}
                                                                                className={`w-full flex items-center gap-2.5 px-3 py-2 text-xs text-gray-800 hover:bg-[#f0f8ff] transition-colors ${idx !== 0 ? 'border-t border-gray-50' : ''}`}>
                                                                                <Wrench size={11} className="text-gray-400 flex-shrink-0" />
                                                                                <span className="flex-1 text-left truncate">{eq}</span>
                                                                                <span className="text-[9px] text-gray-300 flex-shrink-0">+ Add</span>
                                                                            </button>
                                                                        ))}
                                                                        {showAdd && (
                                                                            <button type="button"
                                                                                onClick={e => { e.stopPropagation(); setForm({ ...form, equipmentNames: [...form.equipmentNames, equipSearch.trim()] }); setEquipSearch(''); }}
                                                                                className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-[#0084CF] hover:bg-[#f0f8ff] transition-colors border-t border-dashed border-[#E3F0F9]">
                                                                                <Plus size={11} className="flex-shrink-0" />
                                                                                <span>Add custom: <strong>"{equipSearch.trim()}"</strong></span>
                                                                            </button>
                                                                        )}
                                                                    </>
                                                                );
                                                            })()}
                                                        </div>
                                                        {inventoryEquipments.length > 0 && (
                                                            <div className="px-3 py-1.5 border-t border-gray-100 bg-gray-50/60 flex items-center justify-between">
                                                                <span className="text-[9px] text-gray-400">{inventoryEquipments.length} items in inventory</span>
                                                                {form.equipmentNames.length > 0 && (
                                                                    <span className="text-[9px] text-[#0084CF] font-semibold">{form.equipmentNames.length} selected</span>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Priority */}
                                    <div>
                                        <label className="text-[11px] font-black text-gray-700 uppercase mb-1 block" style={{ fontWeight: 900 }}>Priority</label>
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                            {(['low', 'medium', 'high', 'critical'] as const).map(p => (
                                                <button key={p} type="button" onClick={() => setForm({ ...form, priority: p })}
                                                    className={`flex-1 py-1.5 rounded-xl text-xs font-black capitalize transition-all border-2 ${form.priority === p
                                                        ? p === 'critical' ? 'bg-red-500 text-white border-red-500'
                                                            : p === 'high' ? 'bg-orange-500 text-white border-orange-500'
                                                                : p === 'medium' ? 'bg-amber-400 text-white border-amber-400'
                                                                    : 'bg-green-500 text-white border-green-500'
                                                        : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}
                                                    style={{ fontWeight: 900 }}>
                                                    {p}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Recurring */}
                                    <div className="flex items-center gap-3">
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <div onClick={() => setForm({ ...form, isRecurring: !form.isRecurring })}
                                                className={`w-9 h-5 rounded-full transition-colors flex items-center px-0.5 ${form.isRecurring ? 'bg-[#0084CF]' : 'bg-gray-300'}`}>
                                                <div className={`w-4 h-4 bg-white rounded-full shadow transition-transform ${form.isRecurring ? 'translate-x-4' : 'translate-x-0'}`}></div>
                                            </div>
                                            <span className="text-sm font-black text-gray-700" style={{ fontWeight: 900, WebkitTextStroke: '0.3px currentColor' }}>Recurring</span>
                                        </label>
                                        {form.isRecurring && (
                                            <select value={form.recurringPattern} onChange={e => setForm({ ...form, recurringPattern: e.target.value })}
                                                className="flex-1 border border-gray-200 rounded-xl px-3 py-1.5 text-sm text-black font-semibold outline-none"
                                                style={{ fontWeight: 600, color: 'black' }}>
                                                <option value="">Select pattern</option>
                                                {['Daily', 'Weekly', 'Bi-weekly', 'Monthly', 'Quarterly', 'Yearly'].map(p => <option key={p} value={p}>{p}</option>)}
                                            </select>
                                        )}
                                    </div>

                                    {/* Description */}
                                    <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                                        placeholder="Additional notes / instructions…"
                                        rows={2}
                                        className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-black font-semibold outline-none focus:ring-2 focus:ring-[#0084CF]/30 resize-none placeholder-gray-400"
                                        style={{ fontWeight: 600, color: 'black' }} />
                                </div>
                                {/* Footer */}
                                <div className="px-4 pb-4 pt-3 flex justify-end gap-3 border-t border-gray-100 bg-white">
                                    <button onClick={() => setShowModal(false)}
                                        className="px-4 py-2 border border-gray-200 rounded-xl text-sm font-black text-gray-600 hover:bg-gray-50 transition-colors"
                                        style={{ fontWeight: 900 }}>
                                        Cancel
                                    </button>
                                    <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.98 }}
                                        onClick={saveTask}
                                        disabled={!form.title || !form.date}
                                        className="px-5 py-2 bg-gradient-to-r from-[#0084CF] to-[#1C4D88] text-white rounded-xl text-sm font-black shadow-md disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg transition-all"
                                        style={{ fontWeight: 900 }}>
                                        {editTask ? 'Update Task' : 'Create Task'}
                                    </motion.button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Click outside to close date picker */}
                {showDatePicker && <div className="fixed inset-0 z-40" onClick={() => setShowDatePicker(false)} />}
                {/* Click outside to close equipment dropdown */}
                {showEquipDropdown && <div className="fixed inset-0 z-40" onClick={() => setShowEquipDropdown(false)} />}
            </div>
        </div>
    );
};

export default PMCalendar;
